import { Badge, Search } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import { useMedHistoryConditionSearch } from "@/features/Charting/network/useMedHistoryConditionSearch";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import type { SnomedCode } from "./useAmazonVisitIntake";

export const CiqConditionSearch = ({
  patientId,
  defaultSearchTerm,
  onAdd,
  isPending,
  addedCode,
  pendingCodeDisplay,
}: {
  patientId: string;
  defaultSearchTerm: string;
  onAdd: (code: SnomedCode) => void;
  isPending: boolean;
  addedCode?: SnomedCode;
  pendingCodeDisplay?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const { isLoading: isSearchLoading, data: searchResults } =
    useMedHistoryConditionSearch({ searchTerm, patientId });

  const searchOptions =
    searchResults?.map((r) => ({
      label: r.conditionDisplay,
      value: r.conditionCode,
      metadata: { system: r.conditionSystem },
    })) ?? [];

  if (isPending) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{pendingCodeDisplay}</BodyText>
        <Button
          size="xs"
          type="button"
          isLoading={true}
          className="shrink-0 transition-none bg-button-superclinical text-button-primary border border-ai"
        >
          Add
        </Button>
      </div>
    );
  }

  if (addedCode) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{addedCode.display}</BodyText>
        <div className="flex items-center shrink-0" style={{ height: 25 }}>
          <Badge variant="ai">
            <BodyText size="xs">Added</BodyText>
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <Search
        input={searchTerm}
        leftIcon={SearchIcon}
        isLoading={isSearchLoading}
        showLoadingSpinner
        options={searchOptions}
        placeholder="Search to add..."
        onInputChange={(val) => setSearchTerm(val)}
        onItemSelected={(item) =>
          onAdd({
            code: item.value?.toString() ?? "",
            display: item.label ?? "",
            system: (item.metadata as { system?: string })?.system ?? "",
          })
        }
      />
    </div>
  );
};
